// web/src/routes/Library.tsx
// Task 31: Library route — lists the signed-in user's saved roasts.
// Queries Firestore `roasts` where ownerUid == currentUser uid (newest-first),
// matching the composite index: ownerUid ASC + createdAt DESC.
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { listMyRoasts } from "@/lib/roasts";
import type { RoastArtifact } from "@/types/roast";
import { isPaged } from "@/types/roast";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function Library(): JSX.Element {
  const { user } = useAuth();
  const [roasts, setRoasts] = useState<RoastArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let alive = true;
    setError(null);
    setLoading(true);
    listMyRoasts(user.uid)
      .then((rs) => {
        if (alive) setRoasts(rs);
      })
      .catch(() => {
        if (alive) setError("Failed to load roasts. Please try again.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [user, retryCount]);

  if (loading) {
    return (
      <div className="grid h-[60vh] place-items-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid h-[60vh] place-items-center">
        <div className="text-center space-y-3">
          <p className="text-destructive">{error}</p>
          <button
            className="underline text-muted-foreground"
            onClick={() => setRetryCount((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (roasts.length === 0) {
    return (
      <div className="grid h-[60vh] place-items-center text-muted-foreground">
        No roasts yet.{" "}
        <Link className="ml-2 underline" to="/create">
          Create one
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="library-page mx-auto max-w-5xl p-6">
      <h1 className="mb-4 text-2xl font-bold">Your Roasts</h1>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {roasts.map((r) => {
          const thumb = r.imageUrl ?? (isPaged(r) ? r.keySlides?.[0]?.imageUrl ?? null : null);
          return (
            <Link key={r.id} to={`/r/${r.id}`}>
              <Card className="overflow-hidden transition hover:ring-2 hover:ring-primary">
                {thumb && (
                  <img
                    src={thumb}
                    alt={r.title}
                    className="aspect-video w-full object-cover"
                  />
                )}
                <div className="flex items-center justify-between p-3">
                  <span className="truncate font-medium">{r.title}</span>
                  <Badge variant={r.mode === "roast" ? "destructive" : "default"}>
                    {r.mode}
                  </Badge>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
